import React, { useMemo, useState } from "react";
import { Input } from "../ui/input";
import { Search } from "lucide-react";

/**
 * Reusable data table for Phase 1 sample listings.
 * columns: [{ key, header, render?, align?, mono? }]
 */
export const DataTable = ({ columns, rows, searchKeys = [], testId, emptyLabel = "No records" }) => {
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    if (!query.trim()) return rows;
    const q = query.toLowerCase();
    return rows.filter((row) =>
      searchKeys.some((k) => String(row[k] ?? "").toLowerCase().includes(q)),
    );
  }, [rows, query, searchKeys]);

  return (
    <div className="overflow-hidden rounded-2xl border border-emerald-950/10 bg-white" data-testid={testId}>
      <div className="flex flex-wrap items-center gap-3 border-b border-emerald-950/8 px-4 py-3">
        <div className="relative w-full max-w-xs">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-emerald-900/40" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search…"
            data-testid={`${testId}-search`}
            className="h-9 pl-9"
          />
        </div>
        <div className="ml-auto text-[11px] text-emerald-900/50">
          {filtered.length} / {rows.length} rows
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-emerald-900/5 text-left text-[11px] font-semibold uppercase tracking-wider text-emerald-900/60">
            <tr>
              {columns.map((c) => (
                <th
                  key={c.key}
                  className={`px-4 py-2.5 ${c.align === "right" ? "text-right" : ""}`}
                >
                  {c.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-emerald-950/5">
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-8 text-center text-sm text-emerald-900/50">
                  {emptyLabel}
                </td>
              </tr>
            ) : (
              filtered.map((row, idx) => (
                <tr key={row.id || idx} className="hover:bg-emerald-50/50">
                  {columns.map((c) => (
                    <td
                      key={c.key}
                      className={`px-4 py-3 ${c.align === "right" ? "text-right" : ""} ${
                        c.mono ? "font-mono-tabular" : ""
                      } ${c.strong ? "font-semibold text-emerald-950" : "text-emerald-900/80"}`}
                    >
                      {c.render ? c.render(row) : row[c.key]}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
